import React from 'react'
import { Button, Modal, Select, Spin } from 'antd'
import { inject, observer } from 'mobx-react'

import { auth } from '@services/api'
import { ClientInfo as TranzilaClientInfo } from '../TranzilaPopup'
import {
    Id,
    ProductDetails,
    PURCHASE_PLANS,
    ProductFlowData,
    SelectedSubProduct,
    InsuranceAgentProduct,
    SubscriptionProduct,
    buildProductFlowData,
    buildNonResidentProductFlowData,
    buildSubProductLookup,
    buildSubProductOptionValue,
    buildSubProductOptionLabel,
    buildTranzilaProductDetails,
    calculateClientAge,
    isNonResident,
    formatProductLabel,
    handleDuplicatePaymentCheck
} from '@utils/purchasePopupUtils'
import { PurchaseStore } from 'store/purchaseStore'

type PopupStore = {
    popupType: string
    togglePopup: (value: boolean | string) => void
}

type ProductStore = {
    getInsuranceAgentProductsData: () => Promise<unknown>
    insuranceAgentProducts: InsuranceAgentProduct[]
}

type SubscriptionsStore = {
    getSubscriptions: () => Promise<void>
    subscriptions: SubscriptionProduct[]
}

type ClientInfo = TranzilaClientInfo & {
    birthday?: string
    passport?: string
}

const NON_RESIDENT_PRODUCT_NAME = 'תושבי חוץ'

type PurchasePlan = PURCHASE_PLANS
type PurchaseStep = 1 | 2 | 3

type PurchasePopupState = {
    step: PurchaseStep
    selectedPlan: PurchasePlan | null
    selectedProduct: Id | null
    selectedSubProduct: SelectedSubProduct | null
    selectedSubProductValue: string | null
    clientAge: number | null
    isResettingAfterClose: boolean
    isSubmittingSubProduct: boolean
    isLoadingFlow: boolean
}

type PurchasePopupProps = {
    popupStore: PopupStore
    productStore: ProductStore
    subscriptionsStore: SubscriptionsStore
    productDetails: ProductDetails
    clientInfo: ClientInfo
    showTriggerButton?: boolean
    getClientInfo?: () => Promise<void> | void
    purchaseStore: PurchaseStore
    // When true the final step adds the client to the agent's collective (ACTIVE sub, no payment)
    // instead of opening the Tranzila payment popup.
    collective?: boolean
}

export const PURCHASE_POPUP_TYPE = 'PurchasePopup'
export const COLLECTIVE_POPUP_TYPE = 'CollectivePopup'
export const TRANZILA_POPUP_TYPE = 'TranzilaPopup'
const PURCHASE_MODAL_WIDTH = '850px'

const HEBREW_TEXTS = {
    purchaseTitle: 'רכישת מוצר',
    buttonTitle: 'רכישה',
    collectiveButtonTitle: 'שיוך לקולקטיב',
    monthly: 'מינויים חודשיים',
    bundle: 'בנאדלים למינויים חודשיים',
    stepProducts: 'בחר מוצר',
    subProductPlaceholder: 'בחר מסלול',
    quickBuy: 'לרכישה מהירה',
    back: 'חזרה',
    submit: 'המשך',
    stepPlanSubtitle: 'בחר תוכנית:',
    stepProductSubtitle: 'בחר מוצר:',
    stepSubProductSubtitle: 'בחר מסלול:',
    totalPayment: 'סה"כ לתשלום',
    currencySymbol: '₪'
}

const PURCHASE_STEP_SUBTITLES: Record<PurchaseStep, string> = {
    1: HEBREW_TEXTS.stepPlanSubtitle,
    2: HEBREW_TEXTS.stepProductSubtitle,
    3: HEBREW_TEXTS.stepSubProductSubtitle
}

const PURCHASE_PLAN_OPTIONS: Array<{ plan: PurchasePlan; label: string }> = [
    { plan: PURCHASE_PLANS.MONTHLY, label: HEBREW_TEXTS.monthly },
    { plan: PURCHASE_PLANS.BUNDLE, label: HEBREW_TEXTS.bundle }
]

// Reset only the sub-product selection fields.
function clearSubProductSelection(): Pick<PurchasePopupState, 'selectedSubProduct' | 'selectedSubProductValue'> {
    return {
        selectedSubProduct: null,
        selectedSubProductValue: null
    }
}

// Reset the product selector along with its dependent sub-product state.
function clearProductSelection(): Pick<
    PurchasePopupState,
    'selectedProduct' | 'selectedSubProduct' | 'selectedSubProductValue'
> {
    return {
        selectedProduct: null,
        ...clearSubProductSelection()
    }
}

// Reset the plan choice together with product/sub-product state.
function clearPlanSelection(): Pick<
    PurchasePopupState,
    'selectedPlan' | 'selectedProduct' | 'selectedSubProduct' | 'selectedSubProductValue'
> {
    return {
        selectedPlan: null,
        ...clearProductSelection()
    }
}

function createInitialState(clientInfo: ClientInfo): PurchasePopupState {
    const nonResident = isNonResident(clientInfo)
    return {
        step: nonResident ? 3 : 1,
        clientAge: calculateClientAge(clientInfo),
        ...clearPlanSelection(),
        isResettingAfterClose: false,
        isSubmittingSubProduct: false,
        isLoadingFlow: nonResident
    }
}

@inject((stores: any) => {
    const { popupStore, productStore, subscriptionsStore, purchaseStore } = stores
    return { popupStore, productStore, subscriptionsStore, purchaseStore }
})
@observer
class PurchasePopup extends React.Component<PurchasePopupProps, PurchasePopupState> {
    state: PurchasePopupState = createInitialState(this.props.clientInfo)
    private shouldResetAfterClose = false
    private isMountedFlag = false

    // Collective mode asks for one more confirmation so the agent name is visible before continuing.
    handleTriggerClick = (): void => {
        if (!this.props.collective) {
            this.openPopup()
            return
        }

        Modal.confirm({
            title: HEBREW_TEXTS.collectiveButtonTitle,
            content: (
                <div style={{ direction: 'rtl', textAlign: 'right' }}>
                    <p>
                        <strong>סוכן: </strong>
                        {this.props.clientInfo.agent_name || ''}
                    </p>
                </div>
            ),
            okText: 'אישור',
            cancelText: 'ביטול',
            onOk: () => this.openPopup()
        })
    }

    // Prefetch data and ensure the popup shows without losing current step.
    openPopup = async (): Promise<void> => {
        const { popupStore, productStore, subscriptionsStore, clientInfo, productDetails } = this.props

        // Check for duplicate payment before opening
        if (productDetails && productDetails.id) {
            await handleDuplicatePaymentCheck(auth, clientInfo.id, productDetails.id, () =>
                this.proceedWithPopupOpen(popupStore, productStore, subscriptionsStore, clientInfo)
            )
            return
        }

        this.proceedWithPopupOpen(popupStore, productStore, subscriptionsStore, clientInfo)
    }

    private proceedWithPopupOpen = async (
        popupStore: PopupStore,
        productStore: ProductStore,
        subscriptionsStore: SubscriptionsStore,
        clientInfo: ClientInfo
    ): Promise<void> => {
        // Reset before opening so the user always starts from a clean state,
        // and cannot navigate to step 2 before products finish loading.
        this.setState(createInitialState(clientInfo))
        popupStore.togglePopup(this.props.collective ? COLLECTIVE_POPUP_TYPE : PURCHASE_POPUP_TYPE)
        try {
            await Promise.all([productStore.getInsuranceAgentProductsData(), subscriptionsStore.getSubscriptions()])
            if (!this.isMountedFlag) {
                return
            }
            if (isNonResident(clientInfo)) {
                const nonResidentProduct = productStore.insuranceAgentProducts.find(
                    p => !!p.name && p.name.includes(NON_RESIDENT_PRODUCT_NAME)
                )
                this.setState({
                    selectedProduct:
                        nonResidentProduct && nonResidentProduct.id !== undefined
                            ? nonResidentProduct.id
                            : nonResidentProduct && nonResidentProduct.name
                            ? nonResidentProduct.name
                            : null,
                    isLoadingFlow: false
                })
            }
        } catch (err) {
            console.error('[PurchasePopup] Failed to load product data', err)
            if (this.isMountedFlag) {
                this.setState({ isLoadingFlow: false })
            }
        }
    }

    // Close the modal while keeping the seen step, then reset everything for the next open.
    closePopup = (): void => {
        this.props.popupStore.togglePopup(false)
        this.shouldResetAfterClose = true
    }

    // Jump directly to the Tranzila payment modal after hiding the purchase prompt.
    openTranzilaPopup = (): void => {
        this.props.purchaseStore.setSelectedTranzilaProduct(this.getTranzilaProductDetails())
        // Tell the Tranzila form whether this is a collective (no-charge) flow or a real purchase.
        this.props.purchaseStore.setCollectiveMode(!!this.props.collective)
        this.props.popupStore.togglePopup(TRANZILA_POPUP_TYPE)
    }

    // Choose a plan and clear downstream selections.
    selectPlan = (plan: PurchasePlan): void => {
        this.setState({
            step: 2,
            selectedPlan: plan,
            ...clearProductSelection()
        })
    }

    // Store the chosen product and empty any sub-product value.
    selectProduct = (value: Id): void => {
        this.setState({
            selectedProduct: value,
            ...clearSubProductSelection()
        })
    }

    // Translate the Select option value back into the plan object.
    selectSubProduct = (value: string | number): void => {
        const selectedSubProductValue = String(value)
        const subProductLookup = buildSubProductLookup(this.getProductFlowData().productPlans)
        this.setState({
            selectedSubProduct: subProductLookup[selectedSubProductValue] || null,
            selectedSubProductValue
        })
    }

    // Navigate back to the plan chooser while preserving age.
    goToPlanStep = (): void => {
        const { clientAge } = this.state

        this.setState({
            step: 1,
            clientAge,
            ...clearPlanSelection()
        })
    }

    // Return to the product selector keeping the current plan.
    goToProductStep = (): void => {
        const { clientAge, selectedPlan, selectedProduct, selectedSubProduct, selectedSubProductValue } = this.state

        this.setState({
            step: 2,
            clientAge,
            selectedPlan,
            selectedProduct,
            selectedSubProduct,
            selectedSubProductValue
        })
    }

    // Progress to the sub-product step when a product is chosen.
    submitProduct = (): void => {
        if (!this.state.selectedProduct) {
            return
        }

        this.setState({ step: 3 })
    }

    // Guard the purchase by checking for duplicate payments.
    submitSubProduct = async (): Promise<void> => {
        const { clientInfo } = this.props
        const { selectedSubProduct, isSubmittingSubProduct } = this.state

        if (!selectedSubProduct || !selectedSubProduct.id || isSubmittingSubProduct) {
            return
        }

        // Collective flow: open the Tranzila form to collect details; the actual
        // "add to collective" (no charge) happens when the user submits that form.
        if (this.props.collective) {
            this.openTranzilaPopup()
            return
        }

        this.setState({ isSubmittingSubProduct: true })
        try {
            await handleDuplicatePaymentCheck(auth, clientInfo.id, selectedSubProduct.id, () =>
                this.openTranzilaPopup()
            )
        } finally {
            if (this.isMountedFlag) {
                this.setState({ isSubmittingSubProduct: false })
            }
        }
    }

    // Reuse the previous purchase by jumping straight to Tranzila with the stored product.
    handleQuickBuy = (): void => {
        this.openTranzilaPopup()
    }

    render(): React.ReactNode {
        const { popupStore, showTriggerButton, purchaseStore } = this.props
        const flowData = this.getProductFlowData()
        const isOpen = popupStore.popupType === (this.props.collective ? COLLECTIVE_POPUP_TYPE : PURCHASE_POPUP_TYPE)
        const purchaseStatusVersion = purchaseStore.getPurchaseStatusVersion()
        const { isResettingAfterClose, isLoadingFlow } = this.state

        return (
            <>
                {showTriggerButton && (
                    <Button className="buy-product-button" onClick={this.handleTriggerClick}>
                        {this.props.collective ? HEBREW_TEXTS.collectiveButtonTitle : HEBREW_TEXTS.buttonTitle}
                    </Button>
                )}
                <Modal
                    width={PURCHASE_MODAL_WIDTH}
                    onCancel={this.closePopup}
                    visible={isOpen}
                    afterClose={this.handleModalAfterClose}
                    title={
                        <div>
                            <span>{HEBREW_TEXTS.purchaseTitle}</span>
                        </div>
                    }
                    footer={null}
                >
                    <style>{PURCHASE_POPUP_STYLES}</style>
                    <Spin spinning={isResettingAfterClose || isLoadingFlow} tip="טוען..." size="large" delay={100}>
                        <div
                            className="purchase-popup"
                            style={{
                                ...purchasePopupStyle,
                                opacity: isLoadingFlow ? 0 : 1,
                                pointerEvents: isLoadingFlow ? 'none' : 'auto'
                            }}
                        >
                            <span style={popupTitleStyle}>{HEBREW_TEXTS.purchaseTitle}</span>
                            {this.renderLastProductSummary(purchaseStatusVersion)}
                            <span style={stepSubtitleStyle}>{PURCHASE_STEP_SUBTITLES[this.state.step]}</span>
                            <span style={stepWrapperStyle}>
                                <span style={stepContentStyle}>{this.renderStepContent(flowData)}</span>
                            </span>
                        </div>
                    </Spin>
                </Modal>
            </>
        )
    }

    // Assemble the current flow data based on the selected plan/product.
    private getProductFlowData(): ProductFlowData {
        const { productStore, subscriptionsStore, clientInfo } = this.props
        const { selectedPlan, selectedProduct } = this.state

        if (isNonResident(clientInfo)) {
            return buildNonResidentProductFlowData(selectedProduct, clientInfo, productStore.insuranceAgentProducts)
        }

        return buildProductFlowData(
            selectedPlan,
            selectedProduct,
            clientInfo,
            productStore.insuranceAgentProducts,
            subscriptionsStore.subscriptions
        )
    }

    // Derive the Tranzila payload from the selected sub-product or defaults.
    private getTranzilaProductDetails(): ProductDetails {
        return buildTranzilaProductDetails(this.props.productDetails, this.state.selectedSubProduct)
    }

    // Render the quick-buy summary when a previous purchase exists.
    private renderLastProductSummary(purchaseStatusVersion: number): React.ReactNode {
        const { purchaseStore } = this.props
        if (this.state.step !== 1) {
            return null
        }
        const lastProduct = purchaseStore.getLastProduct()
        if (!purchaseStore.cardCurrentlyBlocked || !lastProduct || !lastProduct.id) {
            return null
        }

        return (
            <span key={purchaseStatusVersion} className="quick-buy-summary" style={quickBuyContainerStyle}>
                <div style={quickBuyDetailsStyle}>
                    <span>{lastProduct.name || ''}</span>
                    {lastProduct.price ? (
                        <span>
                            {HEBREW_TEXTS.totalPayment} {lastProduct.price} {HEBREW_TEXTS.currencySymbol}
                        </span>
                    ) : null}
                </div>
                <Button type="primary" onClick={this.handleQuickBuy}>
                    {HEBREW_TEXTS.quickBuy}
                </Button>
            </span>
        )
    }

    // Render the plan-selection buttons (monthly vs bundle).
    private renderPlanStep(): React.ReactNode {
        return (
            <span style={planButtonsStyle}>
                {PURCHASE_PLAN_OPTIONS.map(planOption => (
                    <Button
                        key={planOption.plan}
                        style={planButtonStyle}
                        onClick={() => this.selectPlan(planOption.plan)}
                    >
                        {planOption.label}
                    </Button>
                ))}
            </span>
        )
    }

    // Render the product dropdown and action buttons for step 2.
    private renderProductStep(flowData: ProductFlowData): React.ReactNode {
        const { selectedPlan, selectedProduct } = this.state

        if (!selectedPlan) {
            return null
        }

        return (
            <div className="purchase-popup-step" style={productStepStyle}>
                <div style={centeredRowStyle}>
                    <Select
                        className="purchase-popup-select"
                        dropdownClassName="purchase-popup-dropdown"
                        style={productSelectStyle}
                        placeholder={HEBREW_TEXTS.stepProducts}
                        value={selectedProduct || undefined}
                        onChange={this.selectProduct}
                    >
                        {flowData.finalProducts.map(option => {
                            const optionValue = option.id !== undefined && option.id !== null ? option.id : option.name

                            return (
                                <Select.Option key={optionValue} value={optionValue}  style={{ direction: 'rtl' }}>
                                    {flowData.isBundlePlan ? formatProductLabel(option) : option.name}
                                </Select.Option>
                            )
                        })}
                    </Select>
                </div>
                <div style={actionButtonsStyle}>
                    <Button style={actionButtonStyle} onClick={this.submitProduct} disabled={!selectedProduct}>
                        {HEBREW_TEXTS.submit}
                    </Button>
                    <Button style={actionButtonStyle} onClick={this.goToPlanStep}>
                        {HEBREW_TEXTS.back}
                    </Button>
                </div>
            </div>
        )
    }

    // Render the sub-product dropdown (plans/prices) for step 3.
    private renderSubProductStep(flowData: ProductFlowData): React.ReactNode {
        const { selectedProduct, selectedSubProduct, selectedSubProductValue } = this.state
        const nonResident = isNonResident(this.props.clientInfo)

        if (!selectedProduct) {
            return null
        }

        return (
            <div className="purchase-popup-step" style={subProductStepStyle}>
                <div style={centeredRowStyle}>
                    <Select
                        className="purchase-popup-select"
                        dropdownClassName="purchase-popup-dropdown"
                        style={subProductSelectStyle}
                        placeholder={HEBREW_TEXTS.subProductPlaceholder}
                        value={selectedSubProductValue || undefined}
                        onChange={this.selectSubProduct}
                    >
                        {flowData.productPlans.map((subProduct, index) => {
                            const optionValue = buildSubProductOptionValue(subProduct, index)

                            return (
                                <Select.Option key={optionValue} value={optionValue} style={{ direction: 'rtl' }}>
                                    {buildSubProductOptionLabel(subProduct, optionValue)}
                                </Select.Option>
                            )
                        })}
                    </Select>
                </div>
                <div style={actionButtonsStyle}>
                    <Button
                        style={actionButtonStyle}
                        onClick={this.submitSubProduct}
                        disabled={!selectedSubProduct || this.state.isSubmittingSubProduct}
                    >
                        {HEBREW_TEXTS.submit}
                    </Button>
                    {!nonResident && (
                        <Button style={actionButtonStyle} onClick={this.goToProductStep}>
                            {HEBREW_TEXTS.back}
                        </Button>
                    )}
                </div>
            </div>
        )
    }

    // Choose the appropriate step component based on state.
    private renderStepContent(flowData: ProductFlowData): React.ReactNode {
        switch (this.state.step) {
            case 1:
                return this.renderPlanStep()
            case 2:
                return this.renderProductStep(flowData)
            case 3:
                return this.renderSubProductStep(flowData)
            default:
                return null
        }
    }

    // Reset the flow state after the modal animation finishes closing.
    private handleModalAfterClose = (forceReset = false): void => {
        if (!forceReset && !this.shouldResetAfterClose) {
            return
        }

        // Skip resetting when the purchase modal closed only because Tranzila just opened.
        if (!forceReset && this.props.popupStore.popupType === TRANZILA_POPUP_TYPE) {
            return
        }

        this.shouldResetAfterClose = false
        this.setState({ isResettingAfterClose: true })
        this.resetStateAfterClose().then(() => {
            this.setState({ isResettingAfterClose: false })
        })
    }

    private resetStateAfterClose(): Promise<void> {
        return new Promise(resolve =>
            this.setState(createInitialState(this.props.clientInfo), () => {
                resolve()
            })
        )
    }

    componentDidMount(): void {
        this.isMountedFlag = true
    }

    componentWillUnmount(): void {
        this.isMountedFlag = false
    }
}

// Styling constants are grouped below so the component logic appears first.
const PURCHASE_POPUP_STYLES = `
    .purchase-popup-dropdown .ant-select-dropdown-menu {
        padding-right: 0px;

    }
    .purchase-popup .ant-select-selection--single .ant-select-selection__rendered {
        margin-right: 8px;
        margin-left: 0px;
        display: flex;
        justify-content: start;
    }
    .purchase-popup .ant-select-selection-selected-value {
        direction: rtl;
        padding-right: 0px;
    }
    .purchase-popup-select .ant-select-arrow {
        right: auto;
        left: 8px;
    }
    .ant-select-selection__placeholder, .ant-select-search__field__placeholder {
        right: 0px;
    }
`

const centeredFlexStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
}

const purchasePopupStyle: React.CSSProperties = {
    width: '100%',
    height: '100%'
}

const popupTitleStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '6px'
}

const quickBuyContainerStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    width: '100%',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '8px'
}

const quickBuyDetailsStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    direction: 'rtl',
    fontSize: '16px',
    gap: '2px'
}

const stepSubtitleStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '10px',
    direction: 'rtl'
}

const stepWrapperStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    width: '100%',
    marginBottom: '20px'
}

const stepContentStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    gap: '10px',
    width: '100%',
    flexDirection: 'column'
}

const planButtonsStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    gap: '10px',
    width: '100%'
}

const productStepStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    gap: '10px',
    width: '250px',
    flexDirection: 'column'
}

const subProductStepStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    gap: '10px',
    width: '100%',
    flexDirection: 'column'
}

const centeredRowStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center'
}

const actionButtonsStyle: React.CSSProperties = {
    ...centeredFlexStyle,
    width: '100%',
    gap: '10px'
}

const planButtonStyle: React.CSSProperties = {
    marginTop: '0px',
    marginRight: '0px',
    marginLeft: '0px'
}

const actionButtonStyle: React.CSSProperties = {
    marginRight: '0px',
    marginLeft: '0px'
}

const productSelectStyle: React.CSSProperties = {
    width: '100%',
    marginBottom: '0px',
    direction: 'rtl'
}

const subProductSelectStyle: React.CSSProperties = {
    width: '450px',
    direction: 'rtl',
    marginBottom: '0px'
}

export default PurchasePopup
